import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { ListPagination } from "@/components/ListPagination";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, FileCheck2, ExternalLink, FileDown, Printer, Trash2, Search, MessageCircle } from "lucide-react";
import { inr, fmtDate, today } from "@/lib/format";
import { printInvoice } from "@/lib/print";
import { toast } from "sonner";

const stripInv = (v) => String(v || "").replace(/^INV[-\s]?/i, "");

export default function BillUpload() {
  const [rows, setRows] = useState(null);
  const [uploadingId, setUploadingId] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkInput = useRef(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [buPageNo, setBuPageNo] = useState(1);
  const [q, setQ] = useState("");
  const [customersList, setCustomersList] = useState([]);
  const [branchContacts, setBranchContacts] = useState({});
  useEffect(() => {
    api.get("/customers").then((r) => setCustomersList(r.data || [])).catch(() => {});
    api.get("/branch-contacts").then((r) => setBranchContacts(r.data || {})).catch(() => {});
  }, []);
  const codeByName = (() => {
    const m = {};
    customersList.forEach((c) => { if (c.name && c.customer_code) m[String(c.name).trim().toLowerCase()] = c.customer_code; });
    return m;
  })();
  const custCode = (r) => r.customer_code || codeByName[String(r.customer_name || "").trim().toLowerCase()] || "";
  const toggleRow = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const fileInputs = useRef({});
  const token = localStorage.getItem("ntc_token");

  const load = async () => {
    try {
      const res = await api.get("/invoices");
      setRows(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const pick = (id) => fileInputs.current[id]?.click();

  const onFile = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/invoices/${id}/bill-copy`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bill copy uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingId("");
      if (fileInputs.current[id]) fileInputs.current[id].value = "";
    }
  };

  const fileUrl = (r) => `${API}/files/${r.bill_copy_path}?auth=${token}`;

  const deleteCopy = async (r) => {
    if (!window.confirm(`Remove the uploaded copy for bill ${stripInv(r.invoice_no)}?`)) return;
    try {
      await api.delete(`/invoices/${r.id}/bill-copy`);
      toast.success("Bill copy removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const onBulkFile = async (e) => {
    const file = e.target.files?.[0];
    const ids = selectedRows.map((r) => r.id);
    if (!file) return;
    if (!ids.length) { toast.error("Select at least one bill first"); return; }
    setBulkUploading(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/invoices/${id}/bill-copy`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) { fail += 1; }
    }
    setBulkUploading(false);
    if (bulkInput.current) bulkInput.current.value = "";
    setSelected(new Set());
    if (ok) toast.success(`Copy attached to ${ok} bill(s)${fail ? `, ${fail} failed` : ""}`);
    else toast.error("Upload failed");
    load();
  };

  const inRange = (r) => {
    const d = (r.issue_date || r.date || r.invoice_date || "").slice(0, 10);
    if (fromDate && (!d || d < fromDate)) return false;
    if (toDate && (!d || d > toDate)) return false;
    if (pendingOnly && r.bill_copy_path) return false;
    const needle = q.trim().toLowerCase();
    if (needle) {
      const hay = [r.customer_name, custCode(r), stripInv(r.invoice_no), r.from_place, r.destination, r.office];
      if (!hay.some((x) => String(x || "").toLowerCase().includes(needle))) return false;
    }
    return true;
  };
  const filtered = (rows || []).filter(inRange);
  const BU_PER_PAGE = 20;
  const buPage = Math.min(buPageNo, Math.max(1, Math.ceil(filtered.length / BU_PER_PAGE)));
  const buOffset = (buPage - 1) * BU_PER_PAGE;
  const pagedFiltered = filtered.slice(buOffset, buOffset + BU_PER_PAGE);
  const vehNo = (r) => r.vehicle_no || [...new Set((r.items || []).map((it) => it.vehicle_no).filter(Boolean))].join(", ");
  const vehType = (r) => r.vehicle_type || [...new Set((r.items || []).map((it) => it.vehicle_type).filter(Boolean))].join(", ");
  const allSel = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected((p) => { const n = new Set(p); if (allSel) filtered.forEach((r) => n.delete(r.id)); else filtered.forEach((r) => n.add(r.id)); return n; });
  const selectedRows = filtered.filter((r) => selected.has(r.id));

  const exportCsv = () => {
    const list = selectedRows.length ? selectedRows : filtered;
    const headers = ["SL No.", "Branch", "Bill No.", "Bill Date", "Customer Name", "Customer Code", "From", "Destination", "Vehicle No.", "Vehicle Type", "Bill Amount", "Bill Status", "Bill Copy"];
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [headers.join(",")];
    list.forEach((r, i) => {
      lines.push([
        i + 1,
        r.office || "Chennai",
        stripInv(r.invoice_no),
        r.issue_date || r.date || r.invoice_date || "",
        r.customer_name || "",
        custCode(r) || "",
        r.from_place || "",
        r.destination || "",
        vehNo(r) || "",
        vehType(r) || "",
        r.total || 0,
        r.status || "",
        r.bill_copy_path ? "Uploaded" : "Pending",
      ].map(cell).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bill-upload-status-${today()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div>
      <PageHeader
        title="Bill Upload"
        subtitle="Attach a scanned/acknowledged copy (image/PDF) to a generated bill. Track which bills still need a copy."
        action={
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <Input
                  className="h-9 w-[190px] pl-8 pr-7"
                  placeholder="Customer / code / bill no."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="billupload-search"
                />
                {q && (
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setQ("")} data-testid="billupload-search-clear">
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-slate-50 px-3 py-2" data-testid="bill-pending-toggle">
              <Switch checked={pendingOnly} onCheckedChange={setPendingOnly} data-testid="bill-pending-only" />
              <Label className="text-xs font-medium text-slate-600">Pending only</Label>
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 w-[150px]" data-testid="bill-from-date" />
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 w-[150px]" data-testid="bill-to-date" />
            </div>
            {(fromDate || toDate) && (
              <Button variant="outline" size="sm" className="h-9" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="bill-clear-dates">Clear</Button>
            )}
            <Button variant="outline" size="sm" className="h-9" onClick={exportCsv} disabled={!filtered.length} data-testid="bill-upload-excel-btn">
              <FileDown className="mr-2 h-4 w-4" /> Excel
            </Button>
            <input ref={bulkInput} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={onBulkFile} data-testid="bulk-bill-file-input" />
            <Button size="sm" className="h-9" onClick={() => bulkInput.current?.click()} disabled={!selectedRows.length || bulkUploading} data-testid="bulk-bill-upload-btn">
              <Upload className="mr-2 h-4 w-4" /> {bulkUploading ? "Uploading…" : `Upload to Selected${selectedRows.length ? ` (${selectedRows.length})` : ""}`}
            </Button>
          </div>
        }
      />

      {rows !== null && (() => {
        const byBranch = {};
        (rows || []).forEach((r) => {
          if (r.bill_copy_path) return;
          const b = r.office || "Chennai";
          if (!byBranch[b]) byBranch[b] = [];
          byBranch[b].push(r);
        });
        const names = Object.keys(byBranch).sort();
        if (!names.length) return null;
        const chaseBranch = (b) => {
          const list = byBranch[b];
          const digits = String(branchContacts[b] || "").replace(/\D/g, "");
          const lines = list
            .slice(0, 30)
            .map((r, i) => `${i + 1}. Bill ${stripInv(r.invoice_no)} — ${r.customer_name || "—"} (₹${(Number(r.total) || 0).toLocaleString("en-IN")})${r.issue_date ? `, dt ${fmtDate(r.issue_date)}` : ""}`);
          const msg = `*NTC — BILL COPY PENDING (${b.toUpperCase()} BRANCH)*\n\n${lines.join("\n")}${list.length > 30 ? `\n…and ${list.length - 30} more` : ""}\n\n*Total: ${list.length} bill(s) without a scanned copy.* Please upload the acknowledged bill copies today.`;
          if (!digits) toast.info(`No WhatsApp number for ${b} — set it in Settings → Branch WhatsApp Contacts. Opening WhatsApp without a number.`);
          window.open(`https://wa.me/${digits ? (digits.length === 10 ? "91" + digits : digits) : ""}?text=${encodeURIComponent(msg)}`, "_blank");
        };
        return (
          <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="billcopy-branch-pills">
            <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">Bill copy pending by branch:</span>
            {names.map((b) => (
              <span key={b} className="inline-flex items-center overflow-hidden rounded-full bg-amber-100">
                <span className="px-2.5 py-1 text-[11px] font-bold text-amber-800" data-testid={`billcopy-pill-${b.toLowerCase()}`}>
                  {b}: {byBranch[b].length} pending
                </span>
                <button
                  type="button"
                  className="border-l border-amber-200 px-2 py-1 text-emerald-700 hover:bg-emerald-50"
                  title={`WhatsApp ${b}'s missing bill-copy list to the branch`}
                  onClick={() => chaseBranch(b)}
                  data-testid={`billcopy-pill-wa-${b.toLowerCase()}`}
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        );
      })()}

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="bill-select-all" aria-label="Select all" />
              </TableHead>
              <TableHead className="w-10 text-[11px] uppercase tracking-[0.12em] text-slate-500">SL No.</TableHead>
              {["Branch", "Bill No.", "Bill Date", "Customer Name", "Customer Code", "From", "Destination", "Vehicle No.", "Vehicle Type", "Bill Amount", "Bill Status", "Bill Copy"].map((h) => (
                <TableHead key={h} className="whitespace-nowrap text-[11px] uppercase tracking-[0.12em] text-slate-500">{h}</TableHead>
              ))}
              <TableHead className="w-[300px] text-right text-[11px] uppercase tracking-[0.12em] text-slate-500">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 11 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={15} className="py-12 text-center text-muted-foreground">
                  No bills{q ? " match your search" : fromDate || toDate ? " in the selected date range" : " available"}.
                </TableCell>
              </TableRow>
            )}
            {rows &&
              pagedFiltered.map((r, i) => (
                <TableRow key={r.id} data-testid="bill-upload-row" className="whitespace-nowrap" data-state={selected.has(r.id) ? "selected" : undefined}>
                  <TableCell className="w-10">
                    <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleRow(r.id)} data-testid="bill-select-row" aria-label="Select row" />
                  </TableCell>
                  <TableCell className="text-sm">{buOffset + i + 1}</TableCell>
                  <TableCell className="text-sm">{r.office || "Chennai"}</TableCell>
                  <TableCell className="text-sm font-medium">{stripInv(r.invoice_no) || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.issue_date || r.date || r.invoice_date)}</TableCell>
                  <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm font-medium" data-testid="billupload-cust-code">{custCode(r) || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="billupload-from-cell">{r.from_place || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="billupload-destination-cell">{r.destination || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="billupload-vehicle-cell">{vehNo(r) || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="billupload-vehicle-type-cell">{vehType(r) || "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.total)}</TableCell>
                  <TableCell className="text-sm">{r.status || "—"}</TableCell>
                  <TableCell>
                    {r.bill_copy_path ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                        <FileCheck2 className="h-3.5 w-3.5" /> Uploaded
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Pending</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <input
                      ref={(el) => (fileInputs.current[r.id] = el)}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      className="hidden"
                      onChange={(e) => onFile(r.id, e)}
                      data-testid={`bill-file-input-${r.id}`}
                    />
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-primary" title="Print bill" onClick={() => printInvoice(r)} data-testid="print-bill-copy-btn">
                        <Printer className="h-3.5 w-3.5" /> Print
                      </Button>
                      {r.bill_copy_path && (
                        <a href={fileUrl(r)} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" data-testid="view-bill-copy-btn">
                            <ExternalLink className="h-3.5 w-3.5" /> View
                          </Button>
                        </a>
                      )}
                      {r.bill_copy_path && (
                        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-red-600" title="Remove uploaded copy" onClick={() => deleteCopy(r)} data-testid="delete-bill-copy-btn">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={r.bill_copy_path ? "outline" : "default"}
                        className="h-8 gap-1 text-xs"
                        disabled={uploadingId === r.id}
                        onClick={() => pick(r.id)}
                        data-testid="upload-bill-btn"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {uploadingId === r.id ? "Uploading…" : r.bill_copy_path ? "Replace" : "Upload Bill"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableShell>
      <ListPagination total={filtered.length} page={buPageNo} setPage={setBuPageNo} pageSize={BU_PER_PAGE} noun="bills" testPrefix="billupload" />
    </div>
  );
}
